import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleTomorrowForecast,
  type HuddleTomorrowForecastResult,
  getHuddleTomorrowForecastOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleTomorrowForecastAppointmentsToTableRows,
  mapHuddleTomorrowForecastSummaryToMetrics,
  type HuddleTomorrowForecastTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleTomorrowForecastOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleTomorrowForecastResult = {
  data: HuddleTomorrowForecastResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  appointmentRows: HuddleTomorrowForecastTableRow[]
}

function useHuddleTomorrowForecastFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleTomorrowForecastOptions & {
  fetcher: HuddleDateFetcher<HuddleTomorrowForecastResult>
}): UseHuddleTomorrowForecastResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: "Failed to load tomorrow's forecast",
  })

  const summaryMetrics = useMemo(
    () => mapHuddleTomorrowForecastSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const appointmentRows = useMemo(
    () => mapHuddleTomorrowForecastAppointmentsToTableRows(data?.appointments),
    [data?.appointments],
  )

  return { data, loading, error, summaryMetrics, appointmentRows }
}

export function useHuddleTomorrowForecast(
  options: UseHuddleTomorrowForecastOptions,
): UseHuddleTomorrowForecastResult {
  return useHuddleTomorrowForecastFromFetcher({
    ...options,
    fetcher: getHuddleTomorrowForecast,
  })
}
export function useHuddleTomorrowForecastOverview(
  options: UseHuddleTomorrowForecastOptions,
): UseHuddleTomorrowForecastResult {
  return useHuddleTomorrowForecastFromFetcher({
    ...options,
    fetcher: getHuddleTomorrowForecastOverview,
  })
}
